import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileGrid } from "@/components/profile/ProfileGrid";
import { EditProfileDialog } from "@/components/profile/EditProfileDialog";
import { FollowListDialog } from "@/components/profile/FollowListDialog";

export default function Profile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [list, setList] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { setList(null); }, [userId]);
  const message = async () => {
    try { const { data } = await api.post("/dm/conversations", { user_id: userId }); navigate(`/mensagens/${data.id}`); } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    setProfile(null); setError(false); setItems([]);
    Promise.all([api.get(`/users/${userId}/profile`), api.get("/shorts", { params: { author_id: userId, limit: 30 } })])
      .then(([p, s]) => { setProfile(p.data); setItems(s.data.items); })
      .catch(() => setError(true));
  }, [userId]);

  if (error) return <p className="py-24 text-center text-muted-foreground" data-testid="profile-not-found">Perfil não encontrado.</p>;
  if (!profile) return <div className="grid place-items-center py-24" data-testid="profile-loading"><Loader2 className="animate-spin text-[hsl(var(--gold))]" /></div>;

  const removed = (s) => {
    setItems((prev) => prev.filter((x) => x.id !== s.id));
    setProfile((p) => ({ ...p, stats: { ...p.stats, posts: p.stats.posts - 1, likes: p.stats.likes - (s.likes || 0) } }));
  };

  const follow = async () => {
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/users/${userId}/follow`);
      setProfile((p) => ({ ...p, is_following: data.following, stats: { ...p.stats, followers: data.followers } }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFollowBusy(false);
    }
  };

  const report = async () => {
    try {
      await api.post(`/users/${userId}/report-avatar`);
      toast.success("Denúncia enviada", { description: "A equipa de moderação vai analisar a foto." });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const avatar = (url) => {
    setProfile((p) => ({ ...p, avatar: url, has_custom_avatar: true }));
    setItems((prev) => prev.map((s) => ({ ...s, author_avatar: url })));
  };

  return (
    <div className="mx-auto max-w-4xl pb-10" data-testid="profile-page">
      <ProfileHeader p={profile} onEdit={() => setEditing(true)} onAvatar={avatar} onFollow={follow} onReport={report} followBusy={followBusy} onList={setList} onMessage={message} />
      <FollowListDialog userId={userId} kind={list} onClose={() => setList(null)} />
      <div className="mb-4 flex items-center gap-2 border-b border-white/10 pb-3 text-sm font-bold">
        <span className="border-b-2 border-[hsl(var(--gold))] pb-3 -mb-[13px]">Publicações</span>
      </div>
      <ProfileGrid items={items} owner={profile.is_me} onRemoved={removed} />
      {profile.is_me && <EditProfileDialog open={editing} onOpenChange={setEditing} profile={profile} onSaved={(d) => setProfile((p) => ({ ...p, ...d }))} />}
    </div>
  );
}
